import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Box,
  Divider,
  Typography,
  TextField,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ResolveIcon,
  Undo as UnresolveIcon,
} from '@mui/icons-material';
import { Note, NoteUpdateRequest } from '../../../../../types/api';
import { useNoteFormState, useNoteOperations } from '../hooks';
import { NoteFormFields, NoteDateFields } from './';
import { getNoteTypeIcon, getNoteColor } from '../../../../../utils/noteIcons';
import { AttachmentList } from '../../maintenance/components/AttachmentList';
import { MarkdownView } from '../../../../../components/Agents/MarkdownView';
import { useErrorHandler } from '../../../../../hooks/useErrorHandler';
import { formatDateISO, formatDateTimeISO } from '../../../../../utils/dateUtils';

interface ViewNoteDialogProps {
  open: boolean;
  onClose: () => void;
  note: Note | null;
  droneSerial: string;
  currentUserId?: string;
  onNoteUpdated?: () => void;
  onNoteDeleted?: () => void;
}

export const ViewNoteDialog: React.FC<ViewNoteDialogProps> = ({
  open,
  onClose,
  note,
  droneSerial,
  currentUserId,
  onNoteUpdated,
  onNoteDeleted,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');

  const { showError, showSuccess } = useErrorHandler();

  const { formData, errors, updateField, validateForm, resetForm, setFormFromNote } =
    useNoteFormState();

  const { updateNote, resolveNote, unresolveNote, deleteNote } = useNoteOperations(droneSerial);

  // Load note data when note changes
  useEffect(() => {
    if (note && open) {
      setFormFromNote(note);
      setResolutionNotes(note.resolution_notes || '');
      setIsEditing(false); // Reset to view mode when opening
    }
  }, [note, open, setFormFromNote]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    if (note) {
      setFormFromNote(note); // Reset form data
    }
    setError(null);
    setIsEditing(false);
  };

  const handleSubmit = async () => {
    if (!note || !validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const updates: NoteUpdateRequest = {
        subject: formData.subject,
        content: formData.content,
        note_type: formData.note_type,
        priority: formData.priority,
      };

      await updateNote(note.id, updates);

      showSuccess('Note updated successfully');
      // Close the dialog after successful save
      onClose();

      if (onNoteUpdated) {
        onNoteUpdated();
      }
    } catch (err) {
      showError(err, 'Failed to update note');
      setError(err instanceof Error ? err.message : 'Failed to update note');
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!note) return;

    try {
      setLoading(true);
      setError(null);

      await resolveNote(note.id, resolutionNotes || undefined);

      showSuccess('Note resolved successfully');
      // Close the dialog after successful resolve
      onClose();

      if (onNoteUpdated) {
        onNoteUpdated();
      }
    } catch (err) {
      showError(err, 'Failed to resolve note');
      setError(err instanceof Error ? err.message : 'Failed to resolve note');
    } finally {
      setLoading(false);
    }
  };

  const handleUnresolve = async () => {
    if (!note) return;

    try {
      setLoading(true);
      setError(null);

      await unresolveNote(note.id);

      showSuccess('Note unresolved successfully');
      // Close the dialog after successful unresolve
      onClose();

      if (onNoteUpdated) {
        onNoteUpdated();
      }
    } catch (err) {
      showError(err, 'Failed to unresolve note');
      setError(err instanceof Error ? err.message : 'Failed to unresolve note');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!note) return;

    if (
      !window.confirm('Are you sure you want to delete this note? This action cannot be undone.')
    ) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await deleteNote(note.id);

      showSuccess('Note deleted successfully');
      onClose();

      if (onNoteDeleted) {
        onNoteDeleted();
      }
    } catch (err) {
      showError(err, 'Failed to delete note');
      setError(err instanceof Error ? err.message : 'Failed to delete note');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      setError(null);
      setResolutionNotes('');
      setIsEditing(false);
      onClose();
    }
  };

  if (!note) return null;

  const isResolved = !!note.resolved_at;
  const canEdit = true; // Allow editing of all notes
  const isAuthor = currentUserId === note.author_id;
  const canDelete = isAuthor || !isResolved;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { maxHeight: '90vh' } }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{isEditing ? 'Edit Note' : 'Note Details'}</Typography>
          {!isEditing && (
            <Box display="flex" gap={1}>
              {canEdit && (
                <IconButton size="small" onClick={handleEdit} disabled={loading}>
                  <EditIcon />
                </IconButton>
              )}
              {!isResolved && (
                <IconButton size="small" color="success" onClick={handleResolve} disabled={loading}>
                  <ResolveIcon />
                </IconButton>
              )}
              {isResolved && (
                <IconButton
                  size="small"
                  color="warning"
                  onClick={handleUnresolve}
                  disabled={loading}
                >
                  <UnresolveIcon />
                </IconButton>
              )}
              {canDelete && (
                <IconButton size="small" color="error" onClick={handleDelete} disabled={loading}>
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 0 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box display="flex" flexDirection="column" gap={isEditing ? 2 : 1.5}>
          {/* Header with type and priority - Read-only view */}
          {!isEditing && (
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              {getNoteTypeIcon(note.note_type, note.priority, 'small')}
              <Chip
                label={note.note_type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                size="small"
                sx={{ backgroundColor: getNoteColor(note.priority), color: 'white' }}
              />
              <Chip
                label={note.priority}
                size="small"
                color={note.priority === 'critical' ? 'error' : 'default'}
              />
              {isResolved && (
                <Chip label="Resolved" size="small" color="success" variant="outlined" />
              )}
            </Box>
          )}

          {/* Form Fields - Editable or Read-only */}
          {isEditing ? (
            <Box
              sx={{
                p: 2,
                border: '2px solid',
                borderColor: 'primary.main',
                borderRadius: 1,
                backgroundColor: '#f8f9ff',
              }}
            >
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 600 }}>
                ✏️ Editing Mode
              </Typography>
              <NoteFormFields
                formData={formData}
                errors={errors}
                onFieldChange={updateField}
                disabled={loading}
              />
              <NoteDateFields
                formData={formData}
                errors={errors}
                onFieldChange={updateField}
                disabled={loading}
              />
            </Box>
          ) : (
            <Box>
              {/* Subject - Read-only */}
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: 600, mb: 1, overflowWrap: 'break-word' }}
              >
                {note.subject}
              </Typography>

              {/* Content - Read-only, rendered as GFM markdown (headers, bold,
                  tables, autolinks). Note: existing plain-text notes are also
                  parsed as markdown, so leading #, "1.", "-" and bare URLs are
                  interpreted as headings/lists/links. */}
              <Box sx={{ mb: 1.5 }}>
                <MarkdownView body={note.content} fontSize="1rem" gfm />
              </Box>

              {/* Date - Read-only */}
              {note.date_of_action && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Date of Action:</strong> {formatDateISO(note.date_of_action)}
                </Typography>
              )}
            </Box>
          )}

          {/* Attachments */}
          {note.attachments && note.attachments.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 0.5 }}>
                Attachments
              </Typography>
              <AttachmentList attachments={note.attachments} compact />
            </Box>
          )}

          {/* Resolution Section - Edit mode */}
          {isEditing && !isResolved && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Resolution
              </Typography>
              <TextField
                label="Resolution Notes (Optional)"
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                disabled={loading}
                multiline
                rows={2}
                fullWidth
                size="small"
                placeholder="Add notes about how this issue was resolved..."
              />
            </Box>
          )}

          {/* Show resolution info if already resolved */}
          {isResolved && (
            <Box sx={{ mt: 1.5 }}>
              <Divider sx={{ mb: 1.5 }} />
              <Box p={1.5} sx={{ backgroundColor: '#e8f5e8', borderRadius: 1 }}>
                <Typography variant="body2" color="success.main" sx={{ fontWeight: 600, mb: 0.5 }}>
                  ✅ Resolved {note.resolved_at && formatDateTimeISO(note.resolved_at)}
                </Typography>
                {note.resolution_notes && (
                  <Typography
                    variant="body2"
                    sx={{ fontStyle: 'italic', overflowWrap: 'break-word' }}
                  >
                    {note.resolution_notes}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Metadata - Read-only */}
          {!isEditing && (
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #e0e0e0' }}>
              <Box display="flex" flexWrap="wrap" gap={2}>
                <Typography variant="caption" color="text.secondary">
                  <strong>By:</strong> {note.author || 'Unknown'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  <strong>Created:</strong> {formatDateTimeISO(note.created_at)}
                </Typography>
                {note.updated_at !== note.created_at && (
                  <Typography variant="caption" color="text.secondary">
                    <strong>Updated:</strong> {formatDateTimeISO(note.updated_at)}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Close
        </Button>

        {isEditing && canEdit && (
          <>
            <Button onClick={handleCancelEdit} disabled={loading}>
              Cancel
            </Button>
            {!isResolved && (
              <Button
                onClick={handleResolve}
                color="success"
                variant="outlined"
                disabled={loading}
                size="small"
                startIcon={loading ? <CircularProgress size={16} /> : null}
              >
                Resolve
              </Button>
            )}
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading || !formData.subject.trim() || !formData.content.trim()}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
